import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import accTickImage from '../assets/acc_tick_iat.png';
import accessLogoTickImage from '../assets/access_logo_tick_iat.png';
import { CustomerForInternalAdminTool } from '../types/CustomerForInternalAdminTool';
import InternalAdminToolService from "../services/InternalAdminToolService";
import CorporateSiteService from "../services/CorporateSiteService";
import styles from './InternalAdminTool.module.scss';

// Import our separate page components
import NoOrganisation from './NoOrganisation';
import InvalidOrganisation from './InvalidOrganisation';
import AlreadyConnectedView from './AlreadyConnectedView';
import SearchView from './SearchView';
import {Alert, Confirm, Spinner} from "@tag/tag-components-react-v4";

function InternalAdminTool() {
    const [searchParams] = useSearchParams();
    const organisationId = searchParams.get('organisationId');
    const evoEnvironment = searchParams.get('evoEnvironment');
    
    const [isValidId, setIsValidId] = useState<boolean>(true);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [foundCustomer, setFoundCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [searchedClientId, setSearchedClientId] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [corporateSite, setCorporateSite] = useState<string>('');
    const [accClient, setAccClient] = useState<string>('');
    const [alert, setAlert] = useState<{type: any, message: string}|undefined>();

    const [linkedCustomer, setLinkedCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isClientSearchLoading, setIsClientSearchLoading] = useState<boolean>(false);

    useEffect(() => {
        initialLoad();
    }, [organisationId]);

    async function initialLoad() {
        setIsLoading(true);

        if (!organisationId || !evoEnvironment) {
            setIsLoading(false)
            return;
        }
        
        const site = await CorporateSiteService.getCorporateSiteByEvoEnvironmentName(evoEnvironment);
        setCorporateSite(site);
        
        const alreadyLinkedCustomer = await InternalAdminToolService.getClientByEvoOrganisationId(organisationId, site);
        if (alreadyLinkedCustomer?.clientId) {
            setLinkedCustomer(alreadyLinkedCustomer);
            setIsLoading(false)
            return;
        }

        const validCustomer = await InternalAdminToolService.checkUninitialisedACCOrganisationExists(organisationId, site);
        if (!validCustomer) {
            setIsValidId(false);
        }

        setIsLoading(false);
    }
    
    const handleLinking = async () => {
        setIsLoading(true);
        
        if (!selectedCustomer || !organisationId || !corporateSite) {
            setIsLoading(false);
            return;
        }

        try {
            const success = await InternalAdminToolService.hookClient(
                selectedCustomer.id,
                organisationId,
                corporateSite
            );

            if (success) {
                setAlert({type: 'success', message: 'Client linked successfully!'});
            }
            else {
                setAlert({type: 'error', message: 'An error occurred while linking the client.'});
            }
        } catch (error) {
            setAlert({type: 'error', message: 'An error occurred while linking the client.'});
        } finally {
            setIsClientSearchLoading(false);
            setSelectedCustomer(null);
            setIsLoading(false)
            initialLoad();
        }
    }

    const handleSearch = async (clientId: string) => {
        setIsClientSearchLoading(true);
        
        // If input is empty, reset search state
        if (!clientId.trim()) {
            setSearchPerformed(false);
            setFoundCustomer(null);
            setSearchedClientId('');
            setIsClientSearchLoading(false);
            return;
        }

        setSearchedClientId(clientId);
        setSearchPerformed(true);

        try {
            const customer = await InternalAdminToolService.getClientByClientIdForInternalAdminTool(parseInt(clientId), corporateSite);
            setFoundCustomer(customer?.clientId ? customer : null);
        } catch (error) {
            console.error('Error searching for client:', error);
            setFoundCustomer(null);
        } finally {
            setIsClientSearchLoading(false);
        }
    };
    
    let content = null;

    if (isLoading) {
        content = <Spinner full={true} />
    }
    else if (!organisationId || !evoEnvironment) {
        content = <NoOrganisation logoSrc={accTickImage} />;
    }
    else if (organisationId && !isValidId) {
        content = <InvalidOrganisation logoSrc={accTickImage} />;
    }
    // If we have a linked customer, show the AlreadyConnectedView
    else if (linkedCustomer) {
      return (
        <AlreadyConnectedView
          organisationId={organisationId}
          accTickImage={accTickImage}
          accessLogoTickImage={accessLogoTickImage}
          customer={linkedCustomer}
          corporateSite={corporateSite}
          setSelectedCustomer={setSelectedCustomer}
        />
      );
    }
    // Otherwise show the search view
    else {
        content = <SearchView
            organisationId={organisationId}
            accTickImage={accTickImage}
            accessLogoTickImage={accessLogoTickImage}
            onSearch={handleSearch}
            searchPerformed={searchPerformed}
            foundCustomer={foundCustomer}
            searchedClientId={searchedClientId}
            loading={isClientSearchLoading}
            accClient={accClient}
            setAccClient={setAccClient}
            corporateSite={corporateSite}
            setSelectedCustomer={setSelectedCustomer}
        />
    }

    return (
        <>
            {content}
            
            <Confirm
                visible={!!selectedCustomer}
                type="question"
                accent="teal"
                heading="Continue"
                onPrimaryButtonClick={() => {
                    handleLinking()
                }}
                onCancelButtonClick={() => {
                    setSelectedCustomer(null);
                }}
                onClose={() => setSelectedCustomer(null)}
            >
                <div>Are you sure you want to connect this ACC customer?</div>
                <br />
                <div><b>AN ERROR MAY RESULT IN A GDPR BREACH</b></div>
                <br />
                {selectedCustomer?.name} to {organisationId}
            </Confirm>

            {alert ?
                <Alert
                    className={styles.Alert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    );
}

export default InternalAdminTool;
